import React from "react";
import PropTypes from "prop-types";

import { connect } from "react-redux";
import {
  autofill,
  change as inputChange,
  formValueSelector,
  Field,
  reduxForm,
} from "redux-form";

import round from "lodash/round";
import { stripCommas } from "lib/utils/numberFunctions";

import { getEntity, getRequiredMinimumDonationAmount } from "data/entity";

import {
  hideAverageAmount,
  setOneTime,
  setOngoingPayments,
  setPaymentInterval,
  setRecurring,
  showAverageAmount,
  getBillingType,
  getPaymentType,
  getPaymentInterval,
  getShowAverageAmount,
} from "data/giving-form/billing";

import { getCampaign } from "data/parent-campaign";

import {
  getAllocationType,
  getFilteredAllocations,
  getSelectedAllocations,
  resetSelectedAllocations,
  setAmountFromForm,
  setToAverageDonation,
  setAmountWithGiftAid,
} from "data/giving-form/allocation-selection";

import Recurring from "../components/Recurring";
import RecurringInfo from "../components/RecurringInfo";
import AmountInput from "../components/AmountInput";
import { FormattedMessage } from "react-intl";

class Billing extends React.Component {
  constructor(props, context) {
    super(props, context);

    this.handleAmountChange = this.handleAmountChange.bind(this);
    this.handleAverageDonation = this.handleAverageDonation.bind(this);
    this.handleMonthly = this.handleMonthly.bind(this);
    this.handleOneTime = this.handleOneTime.bind(this);
    this.handleOngoing = this.handleOngoing.bind(this);
    this.handleQuarterly = this.handleQuarterly.bind(this);
    this.handleAnnually = this.handleAnnually.bind(this);
    this.handleRecurring = this.handleRecurring.bind(this);
  }

  componentDidUpdate() {
    const { dispatch, amount } = this.props;
    const floatAmount = parseFloat(stripCommas(amount));
    dispatch(setAmountWithGiftAid(floatAmount + floatAmount * 0.25));
  }

  handleAverageDonation(e) {
    const { dispatch } = this.props;

    e.preventDefault();

    const { adjustedAmount, amountPerCause } = this.adjustAmount();

    dispatch(setToAverageDonation(adjustedAmount, amountPerCause));
    dispatch(autofill("donation", "amount", String(adjustedAmount)));
    dispatch(hideAverageAmount());
  }

  adjustAmount() {
    const { campaign, selectedAllocations } = this.props;

    const itemCount = selectedAllocations.count() || 1;
    const amount = (campaign.get("average_donation") || 0).toFixed(2);

    const parsedAmount = round(amount);
    const amountPerCause = round(parsedAmount / itemCount, 2);
    // adjust for all the rouding shenanigans
    const adjustedAmount = amountPerCause * itemCount;

    return { adjustedAmount, amountPerCause };
  }

  resetIfChangedByOngoing() {
    const { amount, paymentType, paymentInterval, dispatch } = this.props;

    if (paymentType == "ongoing") {
      if (paymentInterval == "monthly") {
        dispatch(resetSelectedAllocations());
      }

      if (paymentInterval == "quarterly") {
        dispatch(resetSelectedAllocations());
      }

      if (paymentInterval == "anually") {
        dispatch(resetSelectedAllocations());
      }
    }
  }

  // handled by donate form since its connected to the redux forms tore
  handleAmountChange(values) {
    const { dispatch } = this.props;
    const { value } = values;

    this.resetIfChangedByOngoing();

    dispatch(inputChange("donation", "amount", stripCommas(value)));

    if (value > 0) {
      dispatch(setAmountFromForm(stripCommas(value)));
    }
  }

  handleOneTime(e) {
    const { dispatch } = this.props;

    this.resetIfChangedByOngoing();

    dispatch(setOneTime());
    dispatch(showAverageAmount());
    dispatch(inputChange("donation", "occurrences", null));
    dispatch(inputChange("donation", "don_type", "cc"));
  }

  handleRecurring() {
    const { dispatch } = this.props;

    this.resetIfChangedByOngoing();

    dispatch(setRecurring());
    dispatch(hideAverageAmount());
    dispatch(inputChange("donation", "occurrences", 5));
    // set ongoing and monthly as default
    this.handleOngoing();
    this.handleMonthly();
  }

  handleOngoing() {
    const { dispatch } = this.props;

    this.resetIfChangedByOngoing();

    dispatch(setOngoingPayments());
  }

  handleMonthly() {
    const { dispatch, amount } = this.props;

    dispatch(setPaymentInterval("monthly"));

    dispatch(resetSelectedAllocations());
  }

  handleQuarterly() {
    const { dispatch, amount } = this.props;

    dispatch(setPaymentInterval("quarterly"));
    dispatch(resetSelectedAllocations());
  }

  handleAnnually() {
    const { dispatch, amount } = this.props;

    dispatch(setPaymentInterval("annually"));
    dispatch(resetSelectedAllocations());
  }

  renderOngoing() {
    const { paymentType } = this.props;

    if (paymentType === "ongoing") {
      return (
        <div>
          <button data-testid="t-Billing-button" onClick={this.handleMonthly}>
            month
          </button>
          <button data-testid="t-Billing-button-2" onClick={this.handleQuarterly}>
            quarterly
          </button>
          <button data-testid="t-Billing-button-3" onClick={this.handleAnnually}>
            annually
          </button>
        </div>
      );
    }
  }

  renderAverage() {
    const { showAverageAmount, campaign, allocationType, entity } = this.props;

    const { adjustedAmount, amountPerCause } = this.adjustAmount();

    if (!campaign.get("hide_average_donation") && showAverageAmount === true) {
      return (
        <div>
          <span className="uppercase">
            <strong>The average donation is</strong>
          </span>
          <br />
          <span className="billing-amount-text primary-color-text">
            {entity.get("currency_symbol")}
            {adjustedAmount}
          </span>
          <div>
            <button
              data-testid="t-Billing-button-4"
              className="average-button secondary-color-background"
              onClick={this.handleAverageDonation}>
              Donate the Average
            </button>
          </div>
        </div>
      );
    }
  }

  renderInput() {
    const {
      billingType,
      selectedAllocations,
      minimumDonationAmount,
      allocationType,
      currencySymbol,
    } = this.props;

    if (billingType !== "recurring") {
      if (selectedAllocations.count() >= 0 && allocationType == "campaign") {
        return null;
      } else {
        return (
          <div className="position-r">
            <Field
              aria-label="Donation Amount"
              component={AmountInput}
              name="amount"
              className="donation-amount f-1.5x"
              onValueChange={this.handleAmountChange}
              selectedCausesCount={selectedAllocations.count()}
              currencySymbol={currencySymbol}
              validate={(value) => {
                value < minimumDonationAmount
                  ? `Must be at least ${currencySymbol}${minimumDonationAmount}.00`
                  : undefined;
              }}
            />
          </div>
        );
      }
    }
  }

  renderBillingAmount(allowRecurring) {
    const {
      allocationType,
      amount,
      campaign,
      paymentInterval,
      paymentType,
      selectedAllocations,
    } = this.props;

    return (
      <div className="billing-amount">
        <h3 className="m-sm-t">
          <FormattedMessage id="donation" />
        </h3>

        <div className="donation-help">
          <i className="fa fa-gift fa-3x primary-color-text"></i>
          <div className="text-left p-md-l">
            {selectedAllocations.count() >= 0 && allocationType == "campaign" ? (
              <span>You are giving to the following:</span>
            ) : (
              <span>
                <FormattedMessage id="givingTo" /> {campaign.get("name")}.
              </span>
            )}
          </div>
        </div>

        {allowRecurring ? (
          <RecurringInfo
            amount={amount}
            paymentType={paymentType}
            paymentInterval={paymentInterval}
            handleAmountChange={this.handleAmountChange}
            selectedCausesCount={selectedAllocations.count()}
          />
        ) : null}
        <div>
          {this.renderAverage()}
          {this.renderInput()}
        </div>
      </div>
    );
  }

  renderRecurring() {
    const { paymentInterval, paymentType } = this.props;

    return (
      <Recurring
        paymentType={paymentType}
        paymentInterval={paymentInterval}
        onMonthlyInterval={this.handleMonthly}
        onQuarterlyInterval={this.handleQuarterly}
        onAnnuallyInterval={this.handleAnnually}
      />
    );
  }

  renderBillingOptions() {
    const { billingType } = this.props;

    let oneTimeClasses;
    let recurringClasses;

    if (billingType === "recurring") {
      oneTimeClasses = `billing-header`;
      recurringClasses = `billing-header primary-color-background white-font-color`;
    } else {
      oneTimeClasses = `billing-header primary-color-background white-font-color`;
      recurringClasses = `billing-header`;
    }

    return (
      <div className="position-r m-sm-t m-sm-b">
        <button
          data-testid="t-Billing-button-5"
          className={oneTimeClasses}
          onClick={this.handleOneTime}>
          <FormattedMessage id="onTime" />
        </button>
        <div className="billing-circle">
          <div className="billing-circle--circle">
            <FormattedMessage id="or" />
          </div>
        </div>
        <button
          data-testid="t-Billing-button-6"
          className={recurringClasses}
          onClick={this.handleRecurring}>
          <FormattedMessage id="recurring" />
        </button>
      </div>
    );
  }

  render() {
    const {
      allocations,
      allocationType,
      billingType,
      campaign,
      selectedAllocations,
      entity,
    } = this.props;
    const allowBillingAmount =
      (allocations.count() < 1 && selectedAllocations.count() < 0) ||
      allocationType !== "campaign";

    const allowRecurring = billingType === "recurring";
    const billingOptions =
      campaign.get("allow_subscriptions") !== null ||
      campaign.get("allow_stripe_subscriptions") ||
      entity.get("allow_braintree_subscriptions")
        ? this.renderBillingOptions
        : null;

    return (
      <div>
        {billingOptions ? billingOptions.call(this) : null}
        <div className="m-sm-b">
          {allowBillingAmount ? this.renderBillingAmount(allowRecurring) : null}
          {allowRecurring ? this.renderRecurring() : null}
        </div>
      </div>
    );
  }
}

const selector = formValueSelector("donation");

const initialDonationAmount = (state) => {
  if (!!window.PersistentCartService) {
    const totalAmount = Object.values(window.PersistentCartService.allocations()).reduce(
      (total, amount) => parseFloat(total) + (parseFloat(amount) || 0),
      0,
    );
    return totalAmount;
  } else {
    return parseFloat(getEntity(state).get("giving_day_amount") || 0).toFixed(2);
  }
};

const mapStateToProps = (state) => {
  const entity = getEntity(state);
  const activeGatewayCurrencyCode =
    getCampaign(state)("giving_form").get("currency_code");
  const addressDefaults = {
    country: "United States",
  };
  if (activeGatewayCurrencyCode == "CAD") {
    addressDefaults.country = "Canada";
    addressDefaults.region = "Alberta";
  }

  return {
    entity: entity,
    allocations: getFilteredAllocations(state),
    campaign: getCampaign(state)("giving_form"),
    amount: selector(state, "amount"),
    paymentType: getPaymentType(state),
    paymentInterval: getPaymentInterval(state),
    billingType: getBillingType(state),
    showAverageAmount: getShowAverageAmount(state),
    selectedAllocations: getSelectedAllocations(state),
    minimumDonationAmount: getRequiredMinimumDonationAmount(state),
    allocationType: getAllocationType(state),
    currencySymbol: entity.get("currency_symbol"),

    initialValues: {
      amount: initialDonationAmount(state),
      isPublic: true,
      year: "year",
      month: "month",
      address: addressDefaults,
    },
  };
};

Billing = reduxForm({
  form: "donation",
  destroyOnUnmount: false,
  forceUnregisterOnUnmount: true, // <------ unregister fields on unmount
})(Billing);

export default connect(mapStateToProps)(Billing);
